/**
 * The /analytics "Booking panel steps" table: which flows have counts, and the
 * rows of one flow in the order a visitor meets the steps.
 *
 * Order and labels come from the one shared vocabulary the receiver validates
 * a count against (`@layer/canonical-data/booking-panel-steps`), so the page
 * cannot name a step differently from what was counted. A stored name that
 * has since left that list still renders, last, under its raw name: a total
 * that was counted is never dropped from the page.
 */

import {
  BOOKING_PANEL_FLOWS,
  BOOKING_PANEL_STEPS,
  PANEL_VISITOR_CLASSES,
  bookingPanelFlowDef,
  bookingPanelStepDef,
  type BookingPanelStepKind,
  type PanelVisitorClass,
} from "@layer/canonical-data/booking-panel-steps";
import type { PanelStepCountRow } from "./api";

export type PanelStepWindow = 7 | 28;

export interface PanelStepFlowOption {
  readonly flow: string;
  readonly label: string;
  /** Every count of the flow over the longer window. */
  readonly total: number;
}

export interface PanelStepTableRow {
  readonly step: string;
  readonly label: string;
  readonly kind: BookingPanelStepKind;
  readonly counts: Readonly<Record<PanelVisitorClass, number>>;
  readonly total: number;
}

const VISITOR_CLASS_NAMES: ReadonlySet<string> = new Set(
  PANEL_VISITOR_CLASSES.map((c) => c.visitorClass),
);

function isVisitorClass(value: string): value is PanelVisitorClass {
  return VISITOR_CLASS_NAMES.has(value);
}

function countIn(row: PanelStepCountRow, window: PanelStepWindow): number {
  return window === 7 ? row.last_7_days : row.last_28_days;
}

/** The flows that hold a count, in the vocabulary's order, unknown ones last. */
export function panelStepFlows(
  rows: ReadonlyArray<PanelStepCountRow>,
): PanelStepFlowOption[] {
  const totals = new Map<string, number>();
  for (const row of rows) {
    totals.set(row.flow, (totals.get(row.flow) ?? 0) + row.last_28_days);
  }
  const known = BOOKING_PANEL_FLOWS.filter((def) => totals.has(def.flow)).map((def) => ({
    flow: def.flow,
    label: def.label,
    total: totals.get(def.flow) ?? 0,
  }));
  const unknown = [...totals.keys()]
    .filter((flow) => bookingPanelFlowDef(flow) === undefined)
    .sort()
    .map((flow) => ({ flow, label: flow, total: totals.get(flow) ?? 0 }));
  return [...known, ...unknown];
}

/**
 * The flow the table opens on: the trial booking when it has counts, since
 * that is where the ads send people, then a new visitor's booking, then
 * whatever has counts.
 */
export function defaultPanelStepFlow(
  flows: ReadonlyArray<PanelStepFlowOption>,
): string | null {
  for (const preferred of ["trial", "booking"]) {
    if (flows.some((f) => f.flow === preferred)) return preferred;
  }
  return flows[0]?.flow ?? null;
}

/**
 * One flow's rows for one window. A step is listed when it holds a count in
 * the LONGER window, so switching the window changes the numbers and never
 * which rows there are.
 */
export function panelStepTable(
  rows: ReadonlyArray<PanelStepCountRow>,
  flow: string,
  window: PanelStepWindow,
): PanelStepTableRow[] {
  const bySteps = new Map<string, Record<PanelVisitorClass, number>>();
  for (const row of rows) {
    if (row.flow !== flow || !isVisitorClass(row.visitor_class)) continue;
    let counts = bySteps.get(row.step);
    if (counts === undefined) {
      counts = { instagram_app: 0, facebook_app: 0, other_mobile: 0, desktop: 0 };
      bySteps.set(row.step, counts);
    }
    counts[row.visitor_class] += countIn(row, window);
  }
  const toRow = (
    step: string,
    label: string,
    kind: BookingPanelStepKind,
  ): PanelStepTableRow => {
    const counts = bySteps.get(step) ?? {
      instagram_app: 0,
      facebook_app: 0,
      other_mobile: 0,
      desktop: 0,
    };
    return {
      step,
      label,
      kind,
      counts,
      total: counts.instagram_app + counts.facebook_app + counts.other_mobile + counts.desktop,
    };
  };
  const known = BOOKING_PANEL_STEPS.filter((def) => bySteps.has(def.step)).map((def) =>
    toRow(def.step, def.label, def.kind),
  );
  const unknown = [...bySteps.keys()]
    .filter((step) => bookingPanelStepDef(step) === undefined)
    .sort()
    .map((step) => toRow(step, step, "screen"));
  return [...known, ...unknown];
}
